import { Button } from '@mui/material';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import SkeletonLoader from '@components/SkeletonLoader';

import {
  useGetUserAccessManagement,
  useRemoveAllUserAccessSettings,
} from '@hooks/api/access-management';
import { useGetUserNotificationConnections } from '@hooks/api/notification';
import { useIsMobile } from '@hooks/useIsMobile';

const messages = defineMessages({
  removeAllAccess: {
    id: 'accessManagement.removeAllAccess',
    defaultMessage: 'Remove All Access',
  },
  revokeConfirm: {
    id: 'accessManagement.revoke.confirm',
    defaultMessage: 'Are you sure you want to remove all access?',
  },
  message: {
    id: 'accessManagement.revoke.message',
    defaultMessage:
      'This user will no longer be able to access your health records. You can re-enable access at any time.',
  },
});
interface RemoveAccessButtonProps {
  disabled?: boolean;
  ownerUserId?: string;
  accessingUserId?: string;
}

function RemoveAccessButton({
  accessingUserId = '',
  ownerUserId = '',
  disabled = false,
}: RemoveAccessButtonProps) {
  const { formatMessage } = useIntl();
  const [removeAccessSettings, removeAccessSettingsLoading] =
    useRemoveAllUserAccessSettings();
  const { hasNoAccess, loading: getUserAccessManagementLoading } =
    useGetUserAccessManagement({
      user_id: ownerUserId,
      accessing_user_id: accessingUserId,
    });
  const { data: connections, loading: connectionsLoading } =
    useGetUserNotificationConnections({ user_id: ownerUserId });

  const isEmailEnabled = connections.find(
    ({ connected_user_id }) => connected_user_id === accessingUserId
  )?.email_enabled;

  const isMobile = useIsMobile();
  const loading =
    removeAccessSettingsLoading ||
    connectionsLoading ||
    getUserAccessManagementLoading;

  const buttonDisabled = disabled || (hasNoAccess && !isEmailEnabled);

  const handleRemoveAllAccess = async () => {
    if (!ownerUserId || !accessingUserId || buttonDisabled) {
      return;
    }

    await removeAccessSettings({
      owner_user_id: ownerUserId,
      accessing_user_id: accessingUserId,
    });
  };

  return (
    <SkeletonLoader loading={loading} height="30px" width="150px">
      <ConfirmationDialog
        content={formatMessage(messages.message)}
        onConfirm={handleRemoveAllAccess}
        title={formatMessage(messages.revokeConfirm)}
      >
        {({ handleOpen }) => {
          return (
            <Button
              color="secondary"
              disabled={buttonDisabled}
              onClick={handleOpen}
              sx={(theme) => ({
                border: `1px solid ${theme.palette.secondary.main}`,
                backgroundColor: theme.palette.secondary.light,
                color: theme.palette.secondary.main,
                textTransform: 'capitalize',
                height: 'fit-content',
              })}
              fullWidth={isMobile}
              variant="outlined"
            >
              {formatMessage(messages.removeAllAccess)}
            </Button>
          );
        }}
      </ConfirmationDialog>
    </SkeletonLoader>
  );
}

export default withErrorBoundary(RemoveAccessButton, {
  fallback: null,
});
